import { parseContentDispositionFilename } from '@/lib/downloadBlob'
import { client } from './client'
import { statusFilterRequest } from './statusFilterRequest'
import type {
  AzureDevOpsTeam,
  AzureDevOpsField,
  AzureDevOpsIngestionStatus,
  WorkItem,
  CreateWorkItemRequest,
  CreateWorkItemResponse,
  UpdateWorkItemRequest,
  AssignWorkItemRequest,
  AssignWorkItemResponse,
  UpdateWorkItemStatusRequest,
  UpdateWorkItemStatusResponse,
  BulkUpdateWorkItemStatusRequest,
  BulkUpdateWorkItemStatusResponse,
  WorkItemStats,
  WorkItemTranslation,
  PaginationParams,
  PagedResult,
  WorkItemFilters,
  ImportAzureDevOpsWorkItemsRequest,
  ImportAzureDevOpsWorkItemsResponse,
  PushWorkItemsRequest,
  PushWorkItemsResponse,
  AzureDevOpsProcessConfig,
  AzureDevOpsProject,
  AzureDevOpsWorkItemRef,
} from '@/types'

export interface BulkDeleteWorkItemsResponse {
  deletedIds: string[]
  skippedIds: string[]
}

export const workItemsApi = {
  getAll: (projectId: string, { epics, ...rest }: Partial<PaginationParams> & WorkItemFilters = {}) =>
    client.get<PagedResult<WorkItem>>(
      `/projects/${projectId}/work-items`,
      statusFilterRequest(epics?.length ? { ...rest, epic: epics } : rest),
    ).then(r => r.data),

  getById: (projectId: string, id: string) =>
    client.get<WorkItem>(`/projects/${projectId}/work-items/${id}`).then(r => r.data),

  getByNumber: (projectId: string, number: number) =>
    client.get<WorkItem>(`/projects/${projectId}/work-items/by-number/${number}`).then(r => r.data),

  create: (projectId: string, data: CreateWorkItemRequest) =>
    client.post<CreateWorkItemResponse>(`/projects/${projectId}/work-items`, data).then(r => r.data),

  update: (projectId: string, id: string, data: UpdateWorkItemRequest) =>
    client.put(`/projects/${projectId}/work-items/${id}`, data).then(r => r.data),

  delete: (projectId: string, id: string) =>
    client.delete(`/projects/${projectId}/work-items/${id}`).then(r => r.data),

  bulkDelete: (projectId: string, ids: string[]) =>
    client.delete<BulkDeleteWorkItemsResponse>(`/projects/${projectId}/work-items`, { data: { workItemIds: ids } }).then(r => r.data),

  updateStatus: (projectId: string, id: string, data: UpdateWorkItemStatusRequest) =>
    client.put<UpdateWorkItemStatusResponse>(`/projects/${projectId}/work-items/${id}/status`, data).then(r => r.data),

  bulkUpdateStatus: (projectId: string, data: BulkUpdateWorkItemStatusRequest) =>
    client.put<BulkUpdateWorkItemStatusResponse>(`/projects/${projectId}/work-items/status`, data).then(r => r.data),
  
  assign: (projectId: string, id: string, data: AssignWorkItemRequest) =>
    client.put<AssignWorkItemResponse>(`/projects/${projectId}/work-items/${id}/assignee`, data).then(r => r.data),


  getStats: (projectId: string) =>
    client.get<WorkItemStats>(`/projects/${projectId}/work-items/stats`).then(r => r.data),

  getEpics: (projectId: string) =>
    client.get<string[]>(`/projects/${projectId}/work-items/epics`).then(r => r.data),

  translate: (projectId: string, id: string, languageCode: string) =>
    client.post<WorkItemTranslation>(`/projects/${projectId}/work-items/${id}/translations/${languageCode}`)
      .then(r => r.data),

  importFromAzureDevOps: (projectId: string, data: ImportAzureDevOpsWorkItemsRequest) =>
    client.post<ImportAzureDevOpsWorkItemsResponse>(`/projects/${projectId}/work-items/import/azure-devops`, data).then(r => r.data),

  pushToDevOps: (projectId: string, data: PushWorkItemsRequest) =>
    client.post<PushWorkItemsResponse>(`/projects/${projectId}/work-items/push-to-devops`, data).then(r => r.data),

  getAzureDevOpsProcessConfig: (projectId: string) =>
    client.get<AzureDevOpsProcessConfig>(`/projects/${projectId}/work-items/azure-devops/process-config`).then(r => r.data),

  getAzureDevOpsProjects: (projectId: string) =>
    client.get<AzureDevOpsProject[]>(`/projects/${projectId}/work-items/azure-devops/projects`).then(r => r.data),

  getAzureDevOpsTeams: (projectId: string) =>
    client.get<AzureDevOpsTeam[]>(`/projects/${projectId}/work-items/azure-devops/teams`).then(r => r.data),

  getAzureDevOpsWorkItemTypeFields: (projectId: string, workItemType: string) =>
    client.get<AzureDevOpsField[]>(`/projects/${projectId}/work-items/azure-devops/work-item-types/${encodeURIComponent(workItemType)}/fields`).then(r => r.data),

  getAzureDevOpsIngestionStatus: (projectId: string) =>
    client.get<AzureDevOpsIngestionStatus | null>(`/projects/${projectId}/work-items/azure-devops-ingestion/status`).then(r => r.data),

  getAzureDevOpsEpics: (projectId: string, azureProject: string) =>
    client.get<AzureDevOpsWorkItemRef[]>(`/projects/${projectId}/work-items/azure-devops/epics`, { params: { azureProject } }).then(r => r.data),

  getAzureDevOpsFeatures: (projectId: string, azureProject: string, epicId: number) =>
    client.get<AzureDevOpsWorkItemRef[]>(`/projects/${projectId}/work-items/azure-devops/features`, { params: { azureProject, epicId } }).then(r => r.data),

  exportCsv: async (projectId: string, workItemIds: string[]): Promise<{blob: Blob; fileName: string}> => {
    const response = await client.post<Blob>(
      `/projects/${projectId}/work-items/export-csv`,
      { workItemIds },
      { responseType: 'blob' },
    )
    const fileName = parseContentDispositionFilename(
      response.headers['content-disposition'] as string | undefined,
      'work-items.csv',
    )
    return { blob: response.data, fileName }
  },

  exportAllCsv: async (projectId: string, filters: WorkItemFilters): Promise<{blob: Blob; fileName: string}> => {
    const response = await client.post<Blob>(
      `/projects/${projectId}/work-items/export-csv/all`,
      filters,
      { responseType: 'blob' },
    )
    const fileName = parseContentDispositionFilename(
      response.headers['content-disposition'] as string | undefined,
      'work-items.csv',
    )
    return { blob: response.data, fileName }
  },
}
